import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, TrendingDown, Sparkles, CheckCircle2, MessageSquare, Briefcase, ArrowRight, Truck } from 'lucide-react';
import { createOffer } from '../lib/activityStore';
import type { ServiceId } from '../lib/serviceData';
interface Props {
  open: boolean;
  onClose: () => void;
  serviceId: ServiceId;
  serviceName: string;
  prompt?: string;
  routeHeadline: string;
  photos?: string[];
  supplier: {
    name: string;
    image?: string;
    truck?: string;
    price: number;
  };
}
export function RequestBetterOfferModal({
  open,
  onClose,
  serviceId,
  serviceName,
  prompt,
  routeHeadline,
  photos,
  supplier
}: Props) {
  const navigate = useNavigate();
  // Default to a 15% discount as a sensible opening ask.
  const initialAsk = Math.round(supplier.price * 0.85);
  const [target, setTarget] = useState(initialAsk);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  if (!open) return null;
  const minPrice = Math.round(supplier.price * 0.6);
  const maxPrice = supplier.price;
  const discount = supplier.price - target;
  const discountPct = Math.round(discount / supplier.price * 100);
  const handleSubmit = () => {
    createOffer({
      serviceId,
      serviceName,
      prompt: prompt || '',
      routeHeadline,
      supplierName: supplier.name,
      supplierImage: supplier.image,
      supplierTruck: supplier.truck,
      originalPrice: supplier.price,
      requestedPrice: target,
      message: message.trim() || undefined,
      photos
    });
    setSubmitted(true);
  };
  const handleViewOffers = () => {
    onClose();
    // Reset for next open
    setTimeout(() => {
      setSubmitted(false);
      setTarget(initialAsk);
      setMessage('');
    }, 200);
    navigate('/activity', {
      state: {
        initialTab: 'offers'
      }
    });
  };
  return <AnimatePresence data-id="element-503">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-504">
        <motion.div initial={{
        y: 30,
        scale: 0.98
      }} animate={{
        y: 0,
        scale: 1
      }} exit={{
        y: 30,
        scale: 0.98
      }} onClick={e => e.stopPropagation()} className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-505">
          {/* Header */}
          <div className="relative px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-brand-50/60 to-white" data-id="element-506">
            <div className="flex items-center gap-3 min-w-0" data-id="element-507">
              <div className="w-10 h-10 rounded-2xl bg-gray-900 text-white flex items-center justify-center shrink-0" data-id="element-508">
                <TrendingDown className="w-5 h-5" data-id="element-509" />
              </div>
              <div className="min-w-0" data-id="element-510">
                <div className="flex items-center gap-2" data-id="element-511">
                  <h3 className="font-bold text-gray-900" data-id="element-512">
                    {submitted ? 'Offer sent' : 'Request a better offer'}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-brand-700 bg-brand-50 border border-brand-100 px-2 py-0.5 rounded-full" data-id="element-513">
                    <Sparkles className="w-3 h-3" data-id="element-514" />
                    Negotiate
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate" data-id="element-515">
                  Direct with {supplier.name}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 shrink-0" aria-label="Close" data-id="element-516">
              <X className="w-4 h-4" data-id="element-517" />
            </button>
          </div>

          {submitted ?
        // ---- Submitted confirmation state ----
        <div className="p-6" data-id="element-518">
              <div className="w-14 h-14 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center mx-auto mb-4" data-id="element-519">
                <CheckCircle2 className="w-7 h-7" data-id="element-520" />
              </div>
              <h4 className="text-lg font-display font-bold text-gray-900 text-center mb-1" data-id="element-521">
                Offer sent to {supplier.name}
              </h4>
              <p className="text-sm text-gray-500 text-center mb-5" data-id="element-522">
                Their response usually arrives within a few minutes. We'll
                notify you as soon as they reply.
              </p>

              <div className="bg-gray-50 rounded-2xl p-4 space-y-2 mb-5" data-id="element-523">
                <Row label="Original price" value={`AED ${supplier.price}`} muted data-id="element-524" />
                <Row label="Your offer" value={`AED ${target}`} accent="text-brand-700 font-bold" data-id="element-525" />
                <div className="border-t border-gray-200 my-2" data-id="element-526" />
                <Row label="You're asking to save" value={`AED ${discount} (${discountPct}%)`} accent="text-green-700 font-bold" data-id="element-527" />
              </div>

              <div className="bg-blue-50/70 border border-blue-100 rounded-2xl p-3 flex gap-3 mb-5" data-id="element-528">
                <Briefcase className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" data-id="element-529" />
                <p className="text-xs text-blue-900 leading-relaxed" data-id="element-530">
                  <span className="font-bold" data-id="element-531">Track in My Jobs → Offers.</span>{' '}
                  When {supplier.name} responds, you can accept, counter back,
                  or reject — and we'll show a notification in the top nav.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-532">
                <button onClick={onClose} className="flex-1 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold text-sm transition-colors" data-id="element-533">
                  Keep browsing
                </button>
                <button onClick={handleViewOffers} className="flex-1 py-3 bg-gray-900 hover:bg-black text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-1.5" data-id="element-534">
                  View in My Jobs <ArrowRight className="w-4 h-4" data-id="element-535" />
                </button>
              </div>
            </div> :
        // ---- Compose offer state ----
        <div className="p-6" data-id="element-536">
              {/* Supplier card */}
              <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3 mb-5" data-id="element-537">
                <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0" data-id="element-538">
                  <Truck className="w-5 h-5 text-gray-700" data-id="element-539" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-540">
                  <p className="font-semibold text-gray-900 text-sm truncate" data-id="element-541">
                    {supplier.name}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate" data-id="element-542">
                    {supplier.truck || serviceName}
                  </p>
                </div>
                <div className="text-right" data-id="element-543">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold" data-id="element-544">
                    Current price
                  </p>
                  <p className="font-display font-bold text-gray-900" data-id="element-545">
                    AED {supplier.price}
                  </p>
                </div>
              </div>

              {/* Target price input */}
              <div className="mb-5" data-id="element-546">
                <div className="flex items-end justify-between mb-2" data-id="element-547">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-548">
                    Your target price
                  </label>
                  <div className="text-right" data-id="element-549">
                    <p className="text-3xl font-display font-black text-gray-900 leading-none" data-id="element-550">
                      AED {target}
                    </p>
                    <p className="text-[11px] font-semibold text-green-700 mt-0.5" data-id="element-551">
                      Save AED {discount} ({discountPct}%)
                    </p>
                  </div>
                </div>

                <input type="range" min={minPrice} max={maxPrice} step={5} value={target} onChange={e => setTarget(parseInt(e.target.value))} className="w-full accent-brand-500 mb-2" data-id="element-552" />
                <div className="flex justify-between text-[10px] text-gray-400 font-medium" data-id="element-553">
                  <span data-id="element-554">AED {minPrice} (-40%)</span>
                  <span data-id="element-555">AED {maxPrice}</span>
                </div>

                <div className="flex gap-2 mt-3" data-id="element-556">
                  {[10, 15, 20, 25].map(pct => {
                const price = Math.round(supplier.price * (1 - pct / 100));
                const isActive = target === price;
                return <button key={pct} onClick={() => setTarget(price)} className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold border-2 transition-colors ${isActive ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-100 bg-white hover:border-gray-200 text-gray-700'}`} data-id="element-557">
                        –{pct}%
                      </button>;
              })}
                </div>
              </div>

              {/* Message */}
              <div className="mb-5" data-id="element-558">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1" data-id="element-559">
                  <MessageSquare className="w-3 h-3" data-id="element-560" />
                  Message{' '}
                  <span className="font-normal normal-case text-gray-400" data-id="element-561">
                    (optional)
                  </span>
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2} placeholder="E.g. Repeat customer, flexible on timing, can book today…" className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm focus:border-brand-300 resize-none" data-id="element-562" />
              </div>

              {/* AI hint */}
              <div className="bg-brand-50/60 border border-brand-100 rounded-2xl p-3 flex gap-3 mb-5" data-id="element-563">
                <Sparkles className="w-4 h-4 text-brand-600 shrink-0 mt-0.5" data-id="element-564" />
                <p className="text-xs text-brand-900 leading-relaxed" data-id="element-565">
                  <span className="font-bold" data-id="element-566">Tip:</span> Carriers usually
                  counter halfway. A 15% ask gets accepted ~62% of the time on
                  this route.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-567">
                <button onClick={onClose} className="flex-1 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold text-sm transition-colors" data-id="element-568">
                  Cancel
                </button>
                <button onClick={handleSubmit} className="flex-1 py-3 bg-gray-900 hover:bg-black text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2" data-id="element-569">
                  Send offer · AED {target}
                </button>
              </div>
            </div>}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
function Row({
  label,
  value,
  accent,
  muted
}: {
  label: string;
  value: string;
  accent?: string;
  muted?: boolean;
}) {
  return <div className="flex justify-between text-sm" data-id="element-570">
      <span className={muted ? 'text-gray-500 line-through' : 'text-gray-600'} data-id="element-571">
        {label}
      </span>
      <span className={accent || (muted ? 'text-gray-500 line-through' : 'text-gray-900 font-medium')} data-id="element-572">
        {value}
      </span>
    </div>;
}